import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["id"]

import { ref, watch, onMounted, onBeforeUnmount } from "vue";
import { LatLngPair, InitMapOptions } from "@/types";
import type { PropType } from 'vue';
import type { Map, MapEventType, Listener } from 'maplibre-gl';
import { useMap } from "@/composables/maplibre/useMap";
import { usezoomhome } from '@/composables/maplibre/useZoomHome';


// should be using a defined Prop type and widthDefaults, but 
// i could not get that to work with the center (LatLngPair is a weird type)
// but apparently the old options syntax is still good

export default /*@__PURE__*/_defineComponent({
  __name: 'MaplibreMap',
  props: /*@__PURE__*/_mergeModels({
  mapID: {
    type: String as PropType<string>,
    required: true
  },
  home: {
    type: Object as PropType<InitMapOptions>,
    required: false,
    default: () => {
      return {
        loc: [39.8283, -98.5795] as LatLngPair, 
        zoom: 4, 
        t: null
      };
    }
  },
  initial: {
    type: Object as PropType<InitMapOptions>,
    required: false,
    default: null
  },
  showRoads: {
    type: Boolean,
    required: false,
    default: true
  },
  // events: { moveend: handler, zoomend: handler, click: handler, etc. }
  events: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    type: Object as PropType<Record<keyof MapEventType | string, Listener>>,
    default: () => ({})
  },
  // width and height, default to 100%
  width: {
    type: String,
    required: false,
    default: '100%'
  },
  height: {
    type: String,
    required: false,
    default: '100%'
  }
}, {
    "latitude": {
    default: 0
  },
    "latitudeModifiers": {},
    "longitude": {
    default: 0
  },
    "longitudeModifiers": {},
    "zoom": {
    default: 0
  },
    "zoomModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(["ready", "zoomhome"], ["update:latitude", "update:longitude", "update:zoom"]),
  setup(__props, { expose: __expose, emit: __emit }) {

const props = __props;

// define model values that set and return the current map center and zoom
const latitude = _useModel<number>(
  __props, 'latitude');
const longitude = _useModel<number>(
  __props, 'longitude');
const zoom = _useModel<number>(
  __props, 'zoom');




const showRoadsRef = ref(props.showRoads);
watch(() => props.showRoads, (value) => {
  showRoadsRef.value = value;
});

const emit = __emit;






function attachAllEvents(m: Map, handlers: typeof props.events) {
  Object.entries(handlers).forEach(([evt, handler]) => {
    m.on(evt, handler);
  });
}

function detachAllEvents(m: Map, handlers: typeof props.events) {
  Object.entries(handlers).forEach(([evt, handler]) => {
    m.off(evt, handler);
  });
}

const mapReady = ref(false);
// https://stackoverflow.com/a/36072263/11594175
let promiseResolve: (value: Map) => void;
// let promiseReject: (reason?: any) => void; // we won't use the reject
const whenMapReady: Promise<Map> = new Promise((resolve, _reject) => {
  promiseResolve = resolve; /**   this is just javascript, so this pulls 
                                  the resolve function
                                  out of the promise constructor and makes it 
                                  available for us to use
                            **/
  // promiseReject = reject;
});

const onMapReady = (m: Map) => {
  console.log("Map is ready", m);
  mapReady.value = true;
  emit('ready', m);
  promiseResolve(m);
  attachAllEvents(m, props.events);
};

const {
  map,
  createMap,
  setView,
  resetView,
  getCenter,
  cleanupMap
} = useMap(props.mapID, props.initial ?? props.home, showRoadsRef, onMapReady);

watch(() => [latitude.value, longitude.value, zoom.value], ([newLat, newLng, newZoom]) => {
  if (map.value && mapReady.value) {
    setView([newLat, newLng] as LatLngPair, newZoom);
  }
});

onMounted(() => {

  const mMap = createMap(); // just use the return value instead of map.value. eases type checking
  
  usezoomhome(mMap, props.home.loc, props.home.zoom, () => {
    emit('zoomhome');
  });

  mMap.value.on('moveend', () => {
    if (map.value) {
      const center = map.value.getCenter();
      latitude.value = center.lat;
      longitude.value = center.lng;
      zoom.value = map.value.getZoom();
    }
  });
  
});

onBeforeUnmount(() => {
  if (map.value) {
    detachAllEvents(map.value, props.events);
    cleanupMap();
  }
});


// provide only works downwards and i don't think we need it
// const MAPLIBRE_PROVIDE_KEY = props.mapID;
// provide(MAPLIBRE_PROVIDE_KEY, 
const exposedMapApi = {
  map,
  setView,
  resetView,
  getCenter,
  on: (evt: keyof MapEventType | string, handler: Listener) => {
    if (map.value) {
      map.value.on(evt, handler);
    }
  },
  off: (evt: keyof MapEventType | string, handler: Listener) => {
    if (map.value) {
      map.value.off(evt, handler);
    }
  },
  once: (evt: keyof MapEventType | string, handler: Listener) => {
    if (map.value) {
      map.value.once(evt, handler);
    }
  },
  mapReady,
  onReady: whenMapReady
};

// we will also expose these on the template ref
__expose(exposedMapApi);




return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    id: __props.mapID,
    class: "tempo-map",
    style: _normalizeStyle({ '--width': __props.width, '--height': __props.height })
  }, [
    _renderSlot(_ctx.$slots, "default")
  ], 12, _hoisted_1))
}
}

})